import React, {useCallback, useEffect, useRef, useState} from "react";
import {Loader, toaster, Toggle} from 'rsuite';
import './DimmerComponent.scss'
import debounce from "debounce";
import DimmerWorkSection from "./components/DimmerWorkSection";
import DimmerChannelsSection from "./components/DimmerChannelsSection";
import DimmerDeviceDataSection from "./components/DimmerDeviceDataSection";
import DimmerRelaySection from "./components/DimmerRelaySection";
// import DimmerGpsSection from "./components/DimmerGpsSection";
import DimmerPrivateDescriptionSection from "./components/DimmerPrivateDescriptionSection";
import moment from "moment";
import DimmerManualEffectsSection from "./components/DimmerManualEffectsSection";
import DimmerApi from "../../../../../services/Dimmer.api";
import {SharedApi} from "../../../../../services/Shared.api";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import NotificationFactory from "../../../../../shared/Notifications";
import DeviceInfoComponent from "../../../../DeviceInfo/DeviceInfoComponent";
import DeviceEvents from "../../../../shared/DeviceEvents/DeviceEvents";

const DimmerComponent = ({deviceId, deviceType, isAdmin}) => {
  const seedInitialData = (channelCount = 6) => ({
    channels: Array.from({length: channelCount}, (_, index) => ({
      channel: index + 1,
      description: "",
      enabled: false,
      brightness: 0,
      voltage: undefined
    })),
    enabled: false,
    isActive: false,
    description: ""
  })

  const [loading, setLoading] = useState(false);
  const [dimmerState, setDimmerState] = useState(seedInitialData(0));
  const [relayEnabled, setRelayEnabled] = useState(false);
  const [relayMode, setRelayMode] = useState(null);
  const [relayIsActive, setRelayIsActive] = useState(null);
  const [hasRelaySection, setHasRelaySection] = useState(false);
  const [effectDelay, setEffectDelay] = useState(0);
  const [effectFade, setEffectFade] = useState(0);
  const [alwaysEnabled, setAlwaysEnabled] = useState(false);
  const [effectsData, setEffectsData] = useState(null);
  const [timeScheduler, setTimeScheduler] = useState(null);
  const [duskDawnScheduler, setDuskDawnScheduler] = useState(null);
  const [light, setLight] = useState(false);
  const [schedulerMode, setSchedulerMode] = useState('Manual');
  const [effects, setEffects] = useState([]);
  const [effectsLoading, setEffectsLoading] = useState(false);
  const [hasLoadedEffects, setHasLoadedEffects] = useState(false);
  const [isActiveEffects, setIsActiveEffects] = useState(false);
  const [isCircularEffectsMode, setIsCircularEffectsMode] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  // const [gpsCoordinates, setGpsCoordinates] = useState(null);
  // const [shouldShowGpsSection, setShouldShowGpsSection] = useState(false);

  const dimmerStateRef = useRef(dimmerState);

  useEffect(() => {
    dimmerStateRef.current = dimmerState;
  }, [dimmerState]);

  useEffect(() => {
    setLoading(true);
    getDimmerData().then();
    getTimeScheduler().then();
    getDuskDawnScheduler().then();
    setHasLoadedEffects(false);
    setEffects([]);
  }, [deviceId]);

  // const getGpsCoordinates = async (manageLoading = true) => {
  //   if (manageLoading) {
  //     setLoading(true);
  //   }
  //
  //   try {
  //     const result = await DimmerApi.getGpsCoordinates(deviceId);
  //     setGpsCoordinates(result);
  //   } catch (error) {
  //     console.error('Ошибка при получении координат:', error);
  //     toaster.push(
  //       NotificationFactory.error(`Ошибка при получении координат`),
  //       {placement: "topEnd"}
  //     );
  //   } finally {
  //     if (manageLoading) {
  //       setLoading(false);
  //     }
  //   }
  // }

  const getEffects = async () => {
    setEffectsLoading(true);
    setLoading(true);
    try {
      const result = await DimmerApi.getEffects(deviceId);
      setEffects(result || []);
    } catch (error) {
      console.error('Ошибка при получении эффектов:', error);
      toaster.push(
        NotificationFactory.error('Ошибка при получении эффектов'),
        {placement: "topEnd"}
      );
      setEffects([]);
    } finally {
      setEffectsLoading(false);
      setLoading(false);
    }
  };

  const loadEffectsOnce = () => {
    if (!hasLoadedEffects && !effectsLoading) {
      setHasLoadedEffects(true);
      getEffects().then();
    }
  };


  const getDimmerData = async () => {
    setLoading(true);

    try {
      const [response, deviceInfoResponse] = await Promise.all([
        DimmerApi.getDimmerParams(deviceId),
        SharedApi.getDeviceInfo(String(deviceId)),
      ]);

      let channelDescriptions = [];
      if (deviceInfoResponse) {
        channelDescriptions = deviceInfoResponse.details?.channels || [];
        setIsCircularEffectsMode(deviceInfoResponse.details?.isCircularEffectsMode || false);
        setDeviceInfo(deviceInfoResponse);
      }

      if (response.dimmer) {
        const actualChannelCount = response.dimmer.channels?.length || 0;
        const nextHasRelaySection = deviceType !== 83 && actualChannelCount > 2;
        setHasRelaySection(nextHasRelaySection);
        // const nextShouldShowGpsSection = deviceType !== 83 && actualChannelCount > 2;
        //
        // setShouldShowGpsSection(nextShouldShowGpsSection);
        //
        // if (!nextShouldShowGpsSection) {
        //   setGpsCoordinates(null);
        // }

        setDimmerState(prev => {
          const baseChannels = seedInitialData(actualChannelCount).channels;
          const updatedChannels = baseChannels.map((channel, index) => {
            const backendChannel = response.dimmer.channels[index];
            const channelDescription = channelDescriptions.find(desc => desc.channel === channel.channel);

            return {
              ...channel,
              enabled: backendChannel?.isEnabled || false,
              isActive: backendChannel?.isActive || false,
              brightness: backendChannel?.brightness || 0,
              voltage: backendChannel?.voltage || 0,
              description: channelDescription?.description || "",
            };
          });

          return {
            ...prev,
            channels: updatedChannels,
            enabled: response.relay?.relayEnabled || false,
            relayMode: response?.relay?.relayMode,
            isActive: response?.relay?.isActive,
            fadeDivider: response.dimmer.fadeDivider,
            schedulerMode: response.dimmer?.schedulerMode || 'Manual',
          };
        });

        if (nextHasRelaySection) {
          setRelayEnabled(response?.relay?.relayEnabled || false);
          setRelayMode(response?.relay?.relayMode || null);
          setRelayIsActive(response?.relay?.isActive || null);
        } else {
          setRelayEnabled(false);
          setRelayMode(null);
          setRelayIsActive(null);
        }
        setEffectFade(response.dimmer.fadeDivider || false)
        setLight(response.dimmer?.lightEnabled || false);
        setSchedulerMode(response.dimmer?.schedulerMode || 'Manual');
        //
        // if (nextShouldShowGpsSection) {
        //   await getGpsCoordinates(false);
        // }
      }

      if (response.effects) {
        setAlwaysEnabled(response.effects.effectsAlwaysEnabled || false);
        setEffectDelay(response.effects.effectDelay || 0);
        setEffectsData(response.effects);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }


  const sendChannelsState = useCallback(
    async (state) => {
      if (!state?.channels) {
        return;
      }

      const payload = {
        channels: state.channels.map(x => ({
          isEnabled: x.enabled,
          brightness: x.brightness
        }))
      };

      try {
        const result = await DimmerApi.updateChannelsState(deviceId, payload);

        if (result) {
          toaster.push(
            NotificationFactory.success(),
            {placement: "topEnd"}
          );

          getDimmerData().then()
        }
      } catch (e) {
        console.error(e);
        toaster.push(
          NotificationFactory.error(),
          {placement: "topEnd"}
        );
      }
    },
    [deviceId]
  );


  const debouncedSendBrightnessRef = useRef(null);

  useEffect(() => {
    debouncedSendBrightnessRef.current = debounce(sendChannelsState, 500);

    return () => {
      if (debouncedSendBrightnessRef.current) {
        debouncedSendBrightnessRef.current.clear();
      }
    };
  }, [sendChannelsState]);

  const handleToggleChannel = (channelIndex) => {
    setDimmerState(prev => {
      const newChannels = [...prev.channels];
      newChannels[channelIndex] = {
        ...newChannels[channelIndex],
        enabled: !newChannels[channelIndex].enabled
      };

      const newState = {
        ...prev,
        channels: newChannels
      };

      sendChannelsState(newState).then();

      return newState;
    });
  };


  const handleBrightnessChange = (channelIndex, value) => {
    setDimmerState(prev => {
      const newChannels = [...prev.channels];
      newChannels[channelIndex] = {
        ...newChannels[channelIndex],
        brightness: value
      };

      const newState = {
        ...prev,
        channels: newChannels
      };

      if (debouncedSendBrightnessRef.current) {
        debouncedSendBrightnessRef.current(newState);
      }

      return newState;
    });
  };

  const handleDescriptionChange = (channelIndex, value) => {
    setDimmerState(prev => {
      const newChannels = [...prev.channels];
      newChannels[channelIndex] = {
        ...newChannels[channelIndex],
        description: value
      };

      return {
        ...prev,
        channels: newChannels
      };
    });
  };

  const handleUpdateDescription = async (description, channelIndex) => {
    try {
      const updatedChannels = dimmerState.channels.map((channel, index) => ({
        channel: channel.channel,
        description: index === channelIndex ? description : channel.description
      }));

      const payload = {
        channels: updatedChannels
      };

      const result = await DimmerApi.updateChannelDescription(deviceId, payload);

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );

        setDimmerState(prev => {
          const newChannels = [...prev.channels];
          newChannels[channelIndex] = {
            ...newChannels[channelIndex],
            description: description
          };
          return {
            ...prev,
            channels: newChannels
          };
        });
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  };

  const onSavePrivateDescription = async () => {
    try {
      const result = await DimmerApi.updatePrivateDescription(deviceId, '')

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  }

  const handleToggleRelay = async (enabled) => {
    try {
      const result = await DimmerApi.updateRelayState(deviceId, enabled);
      if (result) {
        setRelayEnabled(enabled);
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      } else {
        toaster.push(
          NotificationFactory.error(),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  };

  const handleToggleRelayMode = async (relayMode) => {
    try {
      const result = await DimmerApi.updateRelayMode(deviceId, +relayMode);
      if (result) {
        setRelayMode(relayMode);
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  };

  const handleUpdateEffectDelay = async (delay) => {
    try {
      const result = await DimmerApi.updateEffectDelay(deviceId, delay);

      if (result) {
        setEffectDelay(delay);
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  };

  const handleUpdateEffectFade = async (fade) => {
    try {
      const result = await DimmerApi.updateEffectFade(deviceId, fade);

      if (result) {
        setEffectDelay(fade);
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    }
  };

  const handleModeChange = async (mode) => {
    setLoading(true);
    try {
      const result = await DimmerApi.updateModeChange(deviceId, mode);

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
        setSchedulerMode(mode);
        await getDimmerData();
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error('Ошибка при обновлении режима'),
        {placement: "topEnd"}
      );
      console.error("Ошибка при обновлении режима:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeScheduler = async () => {
    try {
      const response = await DimmerApi.getTimeScheduler(deviceId);
      const offTime = moment(response.offTime, 'HH:mm:ss').toDate();
      const onTime = moment(response.onTime, 'HH:mm:ss').toDate();

      setTimeScheduler([onTime, offTime]);
    } catch (error) {
      console.error(error);
    }
  }

  const getDuskDawnScheduler = async () => {
    try {
      const response = await DimmerApi.getDuskDawnScheduler(deviceId);
      const offTime = moment(response.offTime, 'HH:mm:ss').toDate();
      const onTime = moment(response.onTime, 'HH:mm:ss').toDate();

      setDuskDawnScheduler([offTime, onTime]);
    } catch (error) {
      console.error(error);
    }
  }

  const onToggleLight = async (enabled) => {
    setLoading(true);
    try {
      const result = await DimmerApi.updateLightState(deviceId, enabled)
      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
        await getDimmerData();
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const onToggleEffectsAlwaysEnabled = async (enabled) => {
    setLoading(true);
    try {
      const result = await DimmerApi.updateEffectsAlwaysEnabledState(deviceId, enabled);

      if (result) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
        await getDimmerData();
      }
    } catch (e) {
      console.error(e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
      setLoading(false);
    }
  }

  const handleSaveTimeScheduler = async (timeSchedulerValue) => {
    setLoading(true);
    try {
      const payload = {
        onTime: timeSchedulerValue[0].toLocaleTimeString(),
        offTime: timeSchedulerValue[1].toLocaleTimeString(),
      }

      const response = await DimmerApi.updateTimeScheduler(deviceId, payload);

      if (response) {
        toaster.push(
          NotificationFactory.success(),
          {placement: "topEnd"}
        );
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleUpdateCircularEffects = async (enabled) => {
    setLoading(true)
    try {
      await DimmerApi.updateCircularEffects(deviceId, enabled);
      await getDimmerData();
      toaster.push(
        NotificationFactory.success(`Успешно обновлено`),
        {placement: "topEnd"}
      );
    } catch (e) {
      console.error('Ошибка:', e);
      toaster.push(
        NotificationFactory.error(`Ошибка`),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false)
    }
  };

  return (
    <>
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Блок управления светом</div>
        <section className="secondary-clock">
          {/*<div className="row p-2 pb-0">*/}
          {/*  <div className="col-12 text-end">*/}
          {/*    <UpdateButton loading={loading} onRefresh={''}/>*/}
          {/*  </div>*/}
          {/*</div>*/}
          <div className="row p-2 mt-3">
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control  bg-white mt-0 pt-2 p-1">
                <div className="legend-title">Работа диммера</div>
                <div className="clock-wrapper w-100">
                  <div className="row w-100">
                    <div className="col-md-12 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center mt-2">
                        <Toggle
                          size={"sm"}
                          checked={light}
                          loading={loading}
                          color="blue"
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          onChange={onToggleLight}
                        >
                          <span className="me-2">ВКЛ / ВЫКЛ Свет</span>
                        </Toggle>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="me-2">Эффекты</p>
                        <StatusControl status={isActiveEffects ? "online" : "offline"}/>
                      </div>
                    </div>
                  </div>
                  {/*Работа диммера*/}
                  <DimmerWorkSection
                    loading={loading}
                    effectsData={effectsData}
                    deviceId={deviceId}
                    onModeChange={handleModeChange}
                    timeScheduler={timeScheduler}
                    duskDawnScheduler={duskDawnScheduler}
                    onTimeSchedulerChange={setTimeScheduler}
                    schedulerMode={schedulerMode}
                    onSaveTimeScheduler={handleSaveTimeScheduler}
                  />
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                <div className="legend-title">Данные на диммере</div>
                <div className="clock-wrapper w-100">
                  {/*Данные на диммере*/}
                  <DimmerDeviceDataSection
                    deviceId={deviceId}
                    duskDawnScheduler={duskDawnScheduler}
                    onRefreshDuskDawnScheduler={getDuskDawnScheduler}
                  />
                </div>
              </div>
            </div>
          </div>

          {/*Каналы*/}
          <DimmerChannelsSection
            loading={loading}
            deviceId={deviceId}
            channels={dimmerState.channels}
            onToggleChannel={handleToggleChannel}
            onBrightnessChange={handleBrightnessChange}
            onDescriptionChange={handleDescriptionChange}
            onUpdateDescription={handleUpdateDescription}
            onRefreshData={getDimmerData}
          />

          {/* {shouldShowGpsSection && (
          <div className="row p-2">
            <div className="col-xxl-12 col-lg-12 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                <div className="legend-title mb-3">Приемник GPS/ГЛОНАСС</div>
                <div className="clock-wrapper w-100">
                  <DimmerGpsSection gpsCoordinates={gpsCoordinates}/>
                </div>
              </div>
            </div>
          </div>
        )} */}
          <div className="row p-2">
            <div className="col-xxl-12 col-lg-12 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                <div className="legend-title mb-3">Эффекты</div>
                <div className="clock-wrapper w-100">
                  <DimmerManualEffectsSection
                    channels={dimmerState?.channels}
                    deviceId={deviceId}
                    loading={loading}
                    effects={effects}
                    onOpenEffects={loadEffectsOnce}
                    onRefreshEffects={getEffects}
                    effectsAlwaysEnabled={alwaysEnabled}
                    onToggleEffectsAlwaysEnabled={onToggleEffectsAlwaysEnabled}
                    deviceType={deviceType}
                    onChangeIsActiveEffects={setIsActiveEffects}
                    effectDelay={effectDelay}
                    effectFade={effectFade}
                    onUpdateEffectFade={handleUpdateEffectFade}
                    onUpdateEffectDelay={handleUpdateEffectDelay}
                    schedulerMode={schedulerMode}
                    isCircularEffectsMode={isCircularEffectsMode}
                    onToggleCircularEffectsMode={handleUpdateCircularEffects}/>
                </div>
              </div>
            </div>
          </div>
          <div className="row p-2">
            {hasRelaySection && (
              <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12">
                <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                  <div className="legend-title mb-3">Реле</div>
                  <div className="clock-wrapper w-100">
                    {/*Реле*/}

                    <>
                      <DimmerRelaySection
                        deviceId={deviceId}
                        relayEnabled={relayEnabled}
                        relayMode={relayMode}
                        relayIsActive={relayIsActive}
                        onToggleRelay={handleToggleRelay}
                        onChangeRelayMode={handleToggleRelayMode}
                        loading={loading}
                        timeScheduler={timeScheduler}
                        duskDawnScheduler={duskDawnScheduler}
                      />
                    </>
                  </div>
                </div>
              </div>
            )}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                <div className="legend-title mb-3">Описание</div>
                <div className="clock-wrapper w-100">
                  {/*Описание*/}
                  {deviceInfo ? (
                    <DeviceInfoComponent
                      deviceId={deviceId}
                      deviceType={deviceType}
                      isAdmin={isAdmin}
                      deviceData={deviceInfo}
                    />
                  ) : (
                    <div className="d-flex justify-content-center py-4">
                      <Loader size="md" content="Загрузка информации об устройстве"/>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row p-2">
            <div className="col-xxl-12 col-lg-12 col-md-12 col-sm-12">
              <div className="fieldset-wrapper control bg-white mt-0 pt-0 p-1">
                <div className="legend-title">Описание ( Админ )</div>
                <div className="clock-wrapper w-100">
                  {/*Техническое поле*/}
                  {isAdmin && deviceInfo ? (
                    <DimmerPrivateDescriptionSection
                      deviceId={deviceId}
                      deviceInfo={deviceInfo}
                      onSavePrivateDescription={onSavePrivateDescription}/>
                  ) : isAdmin ? (
                    <div className="d-flex justify-content-center py-4">
                      <Loader size="md" content="Загрузка описания"/>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="row p-2">
            <div className="col-xxl-12 col-lg-12 col-md-12 col-sm-12">
              <DeviceEvents deviceId={deviceId}/>
            </div>
          </div>
        </section>
      </div>


    </>
);
}

export default DimmerComponent;
